import {logger} from '../logger';
import axios from 'axios';
import s from './s';
import {runInAction} from 'mobx';
import {IPriceSummary, ProductState} from './i';
import {imageUploadStore} from './components/image-upload/image-upload.store';
import {encodeNonAsciiChars, getAntiForgeryToken, modal} from '../util';
import {getGradYearAtcError} from './components/grad-year/grad-year-util';
import {getRequiredUVLineError, uvContentJSON} from './components/uv-print/uv-template';
import {createPricingController, isRequestCanceled, PRICING_REQUEST_FAILED_MSG} from './pricing-request/pricing-request';
import {openingFingerprintDiff, readPreloadPrice, reportOpeningPriceMismatch, seedOpeningPrice} from './opening-seed';
import {setPendingAddToCart} from '../global/pending-atc';
import {isAntiforgeryFailure, REFRESH_PAGE_MSG} from '../global/antiforgery';

window.dataLayer = window.dataLayer || [];

export const ATC_REQUEST_FAILED_MSG = 'Unable to add to cart. Please try again.';
export {PRICING_REQUEST_FAILED_MSG};

export type AddToCartResult = {status: 'success'} | {status: 'validation_blocked'} | {status: 'request_failed'; error: string};

let addToCartInFlight: Promise<AddToCartResult> | null = null;
let addToCartSucceeded = false;

/** Clears single-flight bookkeeping (tests + bfcache restore). */
export const resetAddToCartFlight = (): void => {
    addToCartInFlight = null;
    addToCartSucceeded = false;
    runInAction(() => {
        s.ui.atc_busy = false;
    });
};

/**
 * POST helper. Transport failures reject (they must not look like response bodies).
 * Callers that previously relied on the catch-and-return-err contract must handle rejection.
 * Optional AbortSignal is unused by existing callers; canceled requests are not logged as errors.
 */
const req = async (_url: string, o: any, options?: {signal?: AbortSignal}): Promise<any> => {
    const config: {headers: {RequestVerificationToken: string}; signal?: AbortSignal} = {
        headers: {
            RequestVerificationToken: getAntiForgeryToken(),
        },
    };
    if (options?.signal) {
        config.signal = options.signal;
    }

    try {
        const response = await axios.post(_url, o, config);
        return response.data as any;
    } catch (err) {
        if (!isRequestCanceled(err)) {
            logger.error(err);
        }
        throw err;
    }
};

const shopperSafeUploadError = (): string => {
    const msg = s.ui.image_uploaded_msg;
    return typeof msg === 'string' && msg.trim() !== '' ? msg : ATC_REQUEST_FAILED_MSG;
};

const shopperSafeApiError = (value: unknown): string => {
    return typeof value === 'string' && value.trim() !== '' ? value : ATC_REQUEST_FAILED_MSG;
};

const isProductApiResult = (value: unknown): value is ProductAPIResult => {
    return !!value && typeof value === 'object' && typeof (value as ProductAPIResult).success === 'number';
};

const setBusy = (busy: boolean) => {
    runInAction(() => {
        s.ui.atc_busy = busy;
    });
};

const executeAddToCart = async (buyNow: boolean): Promise<AddToCartResult> => {
    setBusy(true);
    runInAction(() => {
        s.ps.atc_error = '';
    });

    try {
        // Template-mode plates: block checkout until every visible (titled) line is filled in.
        // Single chokepoint for both the regular ATC button and the CAF doc-size flow. Surfaced as a
        // modal (a below-button message is easy to miss below the fold) plus inline field highlighting:
        // the flag drives the red asterisk/border in EPTemplate. Other ATC errors are left untouched.
        if (s.epTemplateMissingLineTitles.length > 0) {
            runInAction(() => {
                s.ui.ep_template_show_errors = true;
            });
            modal('modal-ep-template-error');
            return {status: 'validation_blocked'};
        }
        runInAction(() => {
            s.ui.ep_template_show_errors = false;
        });

        const gradYearError = getGradYearAtcError(s.ui.show_grad_year, s.ui.grad_year_input || '', s.ps.grad_year || '', s.prod.YearOfGraduation_Required);
        if (gradYearError) {
            runInAction(() => {
                s.ps.atc_error = gradYearError;
                s.ui.show_caf_doc_sizes = false;
            });
            return {status: 'validation_blocked'};
        }

        // A required UV template (the INFORMS awards): every editable line must be filled.
        const uvLineError = getRequiredUVLineError(s.prod.uv_options, s.prod.uv_lines, s.ps.is_uv_print);
        if (uvLineError) {
            runInAction(() => {
                s.ps.atc_error = uvLineError;
            });
            return {status: 'validation_blocked'};
        }

        // check image upload
        if (s.ps.override_d1_img) {
            runInAction(() => {
                s.ui.image_upload_error = false;
            });
            await imageUploadStore.uploadImage();
            logger.log('uploaded 1');
            if (s.ui.image_upload_error) {
                logger.log(`uploaded 2:${s.ui.image_upload_error}`);
                const error = shopperSafeUploadError();
                runInAction(() => {
                    s.ps.atc_error = error;
                });
                return {status: 'request_failed', error};
            }
            logger.log('uploaded 3');
        }

        let result: unknown;
        try {
            result = await req('/api/atc', getProductPackage());
        } catch (err) {
            // A stale token the retry could not fix (or an account change in another tab) fails every add until a reload.
            const error = isAntiforgeryFailure(err) ? REFRESH_PAGE_MSG : ATC_REQUEST_FAILED_MSG;
            runInAction(() => {
                s.ps.atc_error = error;
            });
            return {status: 'request_failed', error};
        }

        if (!isProductApiResult(result)) {
            runInAction(() => {
                s.ps.atc_error = ATC_REQUEST_FAILED_MSG;
            });
            return {status: 'request_failed', error: ATC_REQUEST_FAILED_MSG};
        }

        if (result.success === 1) {
            const redirectUrl = typeof result.redirect_url === 'string' ? result.redirect_url : '';
            if (!redirectUrl) {
                runInAction(() => {
                    s.ps.atc_error = ATC_REQUEST_FAILED_MSG;
                });
                return {status: 'request_failed', error: ATC_REQUEST_FAILED_MSG};
            }

            runInAction(() => {
                s.ps.atc_error = '';
            });

            // Latch success so a slow redirect cannot accept another POST.
            addToCartSucceeded = true;
            // The cart page pushes GA4 add_to_cart for this line once the cart is loaded.
            setPendingAddToCart({productId: s.prod.ProductID, itemId: s.ps.item_id, qty: s.ps.qty, lineId: Number((result as {cart_line_id?: unknown}).cart_line_id) || undefined});
            window.location.href = buyNow ? `${redirectUrl}?buyNow=1` : redirectUrl;
            return {status: 'success'};
        }

        if (result.success === 0) {
            const error = shopperSafeApiError(result.error);
            runInAction(() => {
                s.ps.atc_error = error;
            });
            return {status: 'request_failed', error};
        }

        runInAction(() => {
            s.ps.atc_error = ATC_REQUEST_FAILED_MSG;
        });
        return {status: 'request_failed', error: ATC_REQUEST_FAILED_MSG};
    } catch (err) {
        logger.error(err);
        runInAction(() => {
            s.ps.atc_error = ATC_REQUEST_FAILED_MSG;
        });
        return {status: 'request_failed', error: ATC_REQUEST_FAILED_MSG};
    } finally {
        // Always clear busy (matches prior per-button finally). The success latch
        // still blocks a duplicate POST if the shopper clicks again before unload.
        setBusy(false);
    }
};

/**
 * Shared single-flight add-to-cart. Concurrent callers share one Promise — one upload and one POST.
 * Busy state lives on s.ui.atc_busy for every ATC control.
 */
export const add_to_cart = async (buyNow = false): Promise<AddToCartResult> => {
    if (addToCartSucceeded) {
        return {status: 'success'};
    }

    if (addToCartInFlight) {
        return addToCartInFlight;
    }

    addToCartInFlight = executeAddToCart(buyNow).finally(() => {
        addToCartInFlight = null;
    });

    return addToCartInFlight;
};

export const redeemCode = async (): Promise<void> => {
    try {
        await req('/api/redeem', getProductPackage());
        runInAction(() => {
            // approved ria
            window.location.href = window.location.href;
            window.location.reload();
        });
    } catch (err) {
        logger.error(err);
    }
};

export const statevent = async (s: IStatEvent): Promise<void> => {
    // axios.post('/api/info/event', s);
};

export const rec_prod = async (): Promise<RecProdResult[]> => {
    // StoreID > 0 selects the API's store-scoped mode: 3 best sellers from this store
    // plus 2 state-seal picks (slots #2/#4) and, when the store has a grad school with a
    // mapped grad-school-type store, that store's top pick (slot #3), with the current
    // product excluded server-side.
    try {
        const result = await req('/api/rec_prod', {
            StoreID: s.si.StoreID,
            ProductID: s.prod.ProductID,
            GradSchoolID: s.currentGradSchoolID,
            hideInstName: false,
        });
        return Array.isArray(result) ? result : [];
    } catch (err) {
        logger.error(err);
        return [];
    }
};

export interface RecProdResult {
    url: string;
    ProductID: number;
    ProductTitle: string;
    price: number;
    IsFastShip: boolean;
    image_url: string;
    image_url_2: string;
    image_url_th: string;
    reviewScore: number;
    reviewCount: number;
    FastShipText: string;
    sku: string;
}

export const prod_cat = async (LinkToProductID: number): Promise<any> => {
    const v = getProductPackage();
    v.ProductID = LinkToProductID;
    try {
        const result: ProductAPIResult = await req('/api/prod_cat', v);
        logger.log(result);
        // prod_cat returns msg/redirect_url and may omit success; keep prior msg read.
        return result && typeof result === 'object' ? result.msg : '';
    } catch (err) {
        logger.error(err);
        return '';
    }
};

// css and css_zoom are the frame preview's layout, about 10 KB each, and DF4's ProductState has no member for them, so
// they stay in the browser. With them (and the old pcs_json copy) every pricing body was about 25 KB, which overflowed
// nginx's request body buffer and was written to a temporary file on every request.
type ProductStatePayload = Omit<ProductState, 'css' | 'css_zoom'>;

// UV print on mat is its own customization, fully separate from engraved plates:
// the resolved uv_lines JSON rides in ps.UVContent and the UV template's EntityID
// in ps.PrintTemplateID (their own ShoppingCart_Products / WebOrder_Products
// columns). EPContent / EPMethodID / ep_id stay engraved-plate-only.
const buildProductStatePayload = (): ProductStatePayload => {
    const {css, css_zoom, ...state} = s.ps;
    const base: ProductStatePayload = {
        ...state,
        EPContent: encodeNonAsciiChars(s.ps.EPContent || ''),
        UVContent: '',
        PrintTemplateID: 0,
    };

    if (s.ps.is_uv_print && s.ps.uv_print_active && Array.isArray(s.prod.uv_lines) && s.prod.uv_lines.length > 0) {
        return {
            ...base,
            UVContent: encodeNonAsciiChars(uvContentJSON(s.prod.uv_lines, s.prod.uv_options)),
            PrintTemplateID: s.prod.uv_template_id || 0,
        };
    }

    return base;
};

const getProductPackage = (): ProductUpdateFromClient => {
    return {
        ProductID: s.prod.ProductID,
        StoreID: s.si.StoreID,
        ps: buildProductStatePayload(),
    };
};

// The server renders the opening price (product.cshtml, `__preload_price`) together with a fingerprint of the
// state it priced. It is applied against the ps of the FIRST /api/pricing payload - after pulse()'s
// normalizations, so exactly what the server is asked to price - never against s.ps inside init(). The request
// still runs and its answer always wins; a seed that disagrees with the live answer is reported, not hidden.
const pricingController = createPricingController({
    getUi: () => s.ui,
    getPayload: () => getProductPackage(),
    requestPricing: (payload, signal) => req('/api/pricing', payload, {signal}),
    onFirstRequest: (ui, payload) => {
        seedOpeningPrice(ui, payload.ps, readPreloadPrice(), {product_id: payload.ProductID, store_id: payload.StoreID});
    },
    onSeededResponse: (ui, price, priceDiscount, payload) => {
        const preload = readPreloadPrice();
        // The shopper changed something before the first answer arrived: that answer prices a different state.
        if (!preload || openingFingerprintDiff(payload.ps, preload.fp).length > 0) {
            return;
        }
        if (price !== ui.price || priceDiscount !== ui.price_discount) {
            reportOpeningPriceMismatch({
                kind: 'price',
                product_id: payload.ProductID,
                store_id: payload.StoreID,
                keys: [],
                seed: {price: ui.price, price_discount: ui.price_discount},
                live: {price, price_discount: priceDiscount},
            });
        }
    },
});

export const pricing = pricingController.pricing;
export const resetPricingFlight = pricingController.resetPricingFlight;

// Back-forward cache can restore this module with a success latch still set, which would
// block every later add-to-cart. Reset when the page is revived from bfcache.
if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
    window.addEventListener('pageshow', (event: PageTransitionEvent) => {
        if (event.persisted) {
            resetAddToCartFlight();
            resetPricingFlight();
        }
    });
}

interface ProductUpdateFromClient {
    ProductID: number;
    StoreID: number;
    ps: ProductStatePayload;
}

interface ProductAPIResult {
    success: number;
    error: string;
    msg: string;
    result_decimal: number;
    result_decimal_discount: number;
    result_int: number;
    item_id: string;
    sku: string;
    redirect_url: string;
    /** MappingID of the cart line /api/atc wrote. */
    cart_line_id?: number;
    price_summary?: IPriceSummary | null;
    /** Upload & Print (FS 10214), /api/pricing only: the store's fee per uploaded image (server rule, 0 when it charges none) and whether result_decimal includes it. */
    upload_fee?: number;
    upload_fee_applied?: boolean;
}

interface IStatEvent {
    d1: string;
    d2: string;
    d3: string;
}
